import { useMemo } from 'react';
import { FormField } from '@src/components/SmartDataTable/types';
import { useUserOptionRender } from '@src/hooks';
import { INVOICE_STATUS_OPTIONS } from '../constants';
import { formatNumber } from '@src/utils/numberUtils';
import { MultilineTextAreaField } from '@src/components/shared/fields';
import dayjs from 'dayjs';

// Types for invoice item calculations (matching DB schema)
interface Offering {
  id: number;
  name: string;
  description: string;
  price: number;
  gst_rate: number;
  total_sessions?: number;
  category?: string;
  duration_in_mins?: number;
}

interface Invoice {
  id: number;
  patient_id: number;
  branch_name?: string;
  doctor_id?: number;
  therapist_id?: number;
  counsellor_id?: number;
  total_amount: number;
  overall_discount_rate?: number;
  overall_discount_amount: number;
  gst: number;
  bill_amount?: number;
  due_amount: number;
  invoice_status: string;
  remark?: string;
  clinicInvoiceItems: InvoiceItem[];
}

interface InvoiceItem {
  id: string | number;
  invoice_id?: number;
  offering_id?: number;
  offering?: any;
  item_name?: string;
  item_description?: string;
  total_sessions?: number;
  session_number?: number;
  unit_price?: number;
  quantity?: number;
  discount?: number;
  gst_rate?: number;
  final_price?: number;
  [key: string]: any;
}

/** Substring used to narrow the offering picker and to validate consultation line items (case-insensitive). */
const CONSULTATION_NAME_SUBSTRING = 'consult';

/** Whether the row’s offering text clearly indicates a consultation-type offering (no ID list). */
function rowMatchesConsultationOffering(row: InvoiceItem): boolean {
  const o = row.offering;
  const name = String(
    (o && typeof o === 'object' && (o as { name?: string }).name) ||
      row.item_name ||
      ''
  ).toLowerCase();
  const description = String(
    (o && typeof o === 'object' &&
      (o as { description?: string }).description) ||
      row.item_description ||
      ''
  ).toLowerCase();
  return name.includes(CONSULTATION_NAME_SUBSTRING) ||
    description.includes(CONSULTATION_NAME_SUBSTRING);
}

export interface UseInvoiceFormFieldsOptions {
  /** Restrict line items to consultation offerings only. */
  consultationOfferingsOnly?: boolean;
}

export const useInvoiceFormFields = (
  patientId?: string | number,
  options?: UseInvoiceFormFieldsOptions
): FormField[] => {
  const consultationOnly = !!options?.consultationOfferingsOnly;

  const userOptionRender = useUserOptionRender({
    showUserType: true,
    size: 'small',
  });

  // Unified calculation function for an invoice item (matches DB constraint)
  const calculateInvoiceItemTotals = (item: InvoiceItem): InvoiceItem => {
    const unitPrice = formatNumber(item.unit_price || 0);
    const quantity = item.quantity || 0;
    const discount = formatNumber(item.discount || 0);
    const gstRate = item.gst_rate || 0;

    // DB Constraint: final_price = round(((unit_price * quantity) * (1 - (discount / 100))) * (1 + (gst_rate / 100)), 2)
    const baseAmount = unitPrice * quantity;
    const discountedAmount = baseAmount * (1 - discount / 100);
    const finalPrice = formatNumber(discountedAmount * (1 + gstRate / 100));

    return {
      ...item,
      unit_price: unitPrice,
      quantity: quantity,
      discount: discount,
      gst_rate: gstRate,
      final_price: finalPrice,
    };
  };

  // Helper function to calculate invoice totals (matching clinic_invoices DB schema)
  const calculateInvoiceTotals = (invoice_items: InvoiceItem[], form: any) => {
    // Filter out deleted items from calculations
    const activeItems = invoice_items.filter(
      (item: any) => item._action !== 'delete'
    );

    // Calculate total_rate (sum of unit_price * quantity for all items - base amount before discount and GST)
    const totalRate = activeItems.reduce((sum, item) => {
      const unitPrice = formatNumber(item.unit_price || 0);
      const quantity = item.quantity || 0;
      return sum + formatNumber(unitPrice * quantity);
    }, 0);

    // Calculate total item-level discount amount
    const totalDiscount = activeItems.reduce((sum, item) => {
      const unitPrice = formatNumber(item.unit_price || 0);
      const quantity = item.quantity || 0;
      const discount = formatNumber(item.discount || 0);
      const baseAmount = unitPrice * quantity;
      const discountAmount = baseAmount * (discount / 100);
      return sum + formatNumber(discountAmount);
    }, 0);

    // Calculate total GST amount (GST is calculated on discounted amounts)
    const gst = activeItems.reduce((sum, item) => {
      const unitPrice = formatNumber(item.unit_price || 0);
      const quantity = item.quantity || 0;
      const discount = formatNumber(item.discount || 0);
      const gstRate = item.gst_rate || 0;

      const baseAmount = unitPrice * quantity;
      const discountedAmount = baseAmount * (1 - discount / 100);
      const gstAmount = discountedAmount * (gstRate / 100);
      return sum + formatNumber(gstAmount);
    }, 0);

    // Calculate total amount: (total rate - total discount) + total tax
    // This matches the SalesFormFields calculation logic
    const totalAmount =
      Math.round((totalRate - totalDiscount + gst) * 100) / 100;

    // Get current overall discount values to preserve user input
    const currentOverallDiscountRate =
      parseFloat(form?.getFieldValue('overall_discount_rate')) || 0;
    const currentOverallDiscountAmount =
      parseFloat(form?.getFieldValue('overall_discount_amount')) || 0;

    // Recalculate bill amount based on existing overall discount
    let newBillAmount = totalAmount;
    let newOverallDiscountAmount = currentOverallDiscountAmount;
    let newOverallDiscountRate = currentOverallDiscountRate;

    if (currentOverallDiscountRate > 0 && totalAmount > 0) {
      // If discount rate exists, recalculate discount amount and bill amount
      newOverallDiscountAmount = formatNumber(
        (totalAmount * currentOverallDiscountRate) / 100
      );
      newBillAmount = formatNumber(totalAmount - newOverallDiscountAmount);
    } else if (currentOverallDiscountAmount > 0 && totalAmount > 0) {
      // If discount amount exists, recalculate discount rate and bill amount
      newOverallDiscountRate = formatNumber(
        (currentOverallDiscountAmount * 100) / totalAmount
      );
      newBillAmount = formatNumber(
        totalAmount - currentOverallDiscountAmount
      );
    } else {
      // No discount, bill amount equals total amount
      newBillAmount = totalAmount;
      newOverallDiscountAmount = 0;
      newOverallDiscountRate = 0;
    }

    // Due amount equals bill amount
    const dueAmount = newBillAmount;

    // Auto-update invoice status based on bill amount
    let invoiceStatus = form?.getFieldValue('invoice_status') || 'UNPAID';
    if (newBillAmount === 0) {
      invoiceStatus = 'PAID';
    } else if (newBillAmount > 0 && invoiceStatus === 'PAID') {
      invoiceStatus = 'UNPAID';
    }

    // Update form fields using clinic_invoices schema structure
    const formUpdates = {
      total_rate: Math.round(totalRate * 100) / 100,
      total_amount: formatNumber(totalAmount),
      overall_discount_rate: Math.round(newOverallDiscountRate * 100) / 100,
      overall_discount_amount: Math.round(newOverallDiscountAmount * 100) / 100,
      gst: formatNumber(gst),
      bill_amount: Math.round(newBillAmount * 100) / 100,
      due_amount: dueAmount,
      invoice_status: invoiceStatus,
    };

    // Method 1: setFieldsValue
    if (form && typeof form.setFieldsValue === 'function') {
      try {
        form.setFieldsValue(formUpdates);
      } catch (error) {
        // Error handling
      }
    }

    // Method 2: setFields (legacy)
    if (form && typeof form.setFields === 'function') {
      try {
        form.setFields([
          { name: 'total_rate', value: Math.round(totalRate * 100) / 100 },
          { name: 'total_amount', value: formatNumber(totalAmount) },
          { name: 'overall_discount_rate', value: Math.round(newOverallDiscountRate * 100) / 100 },
          {
            name: 'overall_discount_amount',
            value: Math.round(newOverallDiscountAmount * 100) / 100,
          },
          { name: 'gst', value: formatNumber(gst) },
          { name: 'bill_amount', value: Math.round(newBillAmount * 100) / 100 },
          { name: 'due_amount', value: dueAmount },
        ]);
      } catch (error) {
        // Error handling
      }
    }

    // Method 3: Force update by triggering form revalidation
    if (form && typeof form.validateFields === 'function') {
      try {
        form
          .validateFields([
            'total_rate',
            'total_amount',
            'overall_discount_rate',
            'overall_discount_amount',
            'gst',
            'bill_amount',
            'due_amount',
          ])
          .catch(() => {});
      } catch (error) {
        // Error handling
      }
    }

    // Method 4: Force re-render by updating form instance
    if (form && typeof form.getFieldsValue === 'function') {
      try {
        const currentValues = form.getFieldsValue();
      } catch (error) {
        // Error handling
      }
    }
  };

  // Common onValueChange handler for all columns
  const handleColumnValueChange = (
    columnKey: string,
    value: any,
    rowData: InvoiceItem,
    allRows: InvoiceItem[],
    form: any
  ) => {
    let updatedRow = { ...rowData };

    switch (columnKey) {
      case 'offering_id':
        // Handle offering selection from offerings
        if (value && value.data) {
          const offering = value.data;
          if (offering) {
            // Always set quantity to 1 when selecting a new offering
            const unitPrice = formatNumber(parseFloat(offering.price) || 0);

            updatedRow = {
              ...rowData,
              offering_id: offering.id,
              offering: {
                id: offering.id,
                name: offering.name,
                description: offering.description,
                price: parseFloat(offering.price),
                gst_rate: offering.gst_rate,
                total_sessions: offering.total_sessions,
                category: offering.category,
                duration_in_mins: offering.duration_in_mins,
              },
              item_name: offering.name,
              item_description: offering.description || '',
              unit_price: unitPrice,
              quantity: 1, // Always set to 1 when selecting new offering
              total_sessions: offering.total_sessions,
              session_number: rowData.session_number,
              discount: formatNumber(offering.discount || 0),
              gst_rate: offering.gst_rate || 0,
            };
          }
        } else {
          // When offering is cleared, reset the related fields to prevent miscalculations
          updatedRow = {
            ...rowData,
            offering_id: undefined,
            offering: undefined,
            item_name: '',
            item_description: '',
            unit_price: 0,
            quantity: 1,
            discount: 0,
            gst_rate: 0,
            total_sessions: 0,
            session_number: 0,
            final_price: 0, // Recalculate to 0
          };
        }
        break;
      case 'unit_price':
      case 'discount':
        // Update the specific field
        updatedRow[columnKey] = formatNumber(value);
        break;
      case 'quantity':
        // Update the specific field - quantity should be at least 1
        updatedRow[columnKey] = Math.max(1, value || 1);
        break;
      case 'session_number':
        // Update the specific field - session_number should be a normal integer
        updatedRow[columnKey] = value;
        break;
      case 'gst_rate':
        // Update the specific field - gst_rate should be a normal integer
        updatedRow[columnKey] = value;
        break;
      case 'total_sessions':
        // Update the specific field - total_sessions should be a normal number
        updatedRow[columnKey] = value;
        break;

      default:
        // For other fields, just update the value
        updatedRow[columnKey] = value;
        break;
    }

    // Recalculate totals for the updated row
    updatedRow = calculateInvoiceItemTotals(updatedRow);

    // Update the row in the table
    const rowIndex = allRows.findIndex(row => row.id === rowData.id);
    if (rowIndex !== -1) {
      allRows[rowIndex] = updatedRow;
      form.setFields([{ name: 'clinic_invoice_items', value: [...allRows] }]);
    }

    // Recalculate totals
    calculateInvoiceTotals(allRows, form);
  };

  return useMemo(
    () => [
      // Hidden fields that go in payload but don't show in form
      {
        name: 'patient_id',
        label: 'Patient ID',
        type: 'text',
        required: false,
        defaultValue: patientId,
        layout: { span: 0 },
        style: { display: 'none' },
        hidden: true,
      },

      {
        name: 'branch_name',
        label: '',
        type: 'text' as const,
        required: false,
        defaultValue: 'Main',
        layout: { span: 0 },
        props: {
          style: { display: 'none' },
        },
        hidden: true,
      },

      // Row 1: Doctor, Therapist, Counsellor
      {
        name: 'doctor_id',
        label: 'Doctor',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select doctor...',
        layout: { span: 8 },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'eq',
              value: 'doctor',
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
      },
      {
        name: 'therapist_id',
        label: 'Therapist',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select therapist...',
        layout: { span: 8 },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'eq',
              value: 'therapist',
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
      },
      {
        name: 'counsellor_id',
        label: 'Counsellor',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select counsellor...',
        layout: { span: 8 },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'eq',
              value: 'counsellor',
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
      },
      {
        name: 'remark',
        label: 'Remark',
        type: 'custom',
        required: false,
        placeholder: 'Enter any remarks...',
        layout: { span: 16 },
        component: MultilineTextAreaField,
        props: {
          rows: 4,
          showCount: true,
          maxLength: 300,
          autoSize: { minRows: 3, maxRows: 3 },
          style: {
            resize: 'vertical',
            fontFamily: 'monospace',
            lineHeight: '1.5',
          },
        },
        tooltip: 'Add any remarks about the invoice. Use Enter for new lines.',
      },

      // Row 3: Invoice Items Table
      {
        name: 'clinic_invoice_items',
        label: consultationOnly ? 'Consultation items' : 'Invoice Items',
        type: 'table',
        required: true,
        validateTrigger: ['onChange', 'onBlur'],
        ...(consultationOnly
          ? {
              validation: {
                validator: async (_rule: any, value: InvoiceItem[] | undefined) => {
                  for (const row of value || []) {
                    if ((row as any)._action === 'delete') continue;
                    const oid = row?.offering_id;
                    if (oid === undefined || oid === null) continue;
                    if (!rowMatchesConsultationOffering(row)) {
                      throw new Error(
                        'Consultation invoices only allow offerings whose name or description includes "consult" (e.g. consultation, consulting, online consulting).'
                      );
                    }
                  }
                },
              },
            }
          : {}),
        tableProps: {
          title: '',
          addButtonText: 'Add Item',
          minRows: 1,
          showRowNumbers: true,
          showAddButton: true,
          addButtonPosition: 'footer',
          defaultRow: {
            id: `temp_${Date.now()}`,
            offering_id: undefined,
            offering: undefined,
            item_name: '',
            item_description: '',
            unit_price: 0,
            quantity: 1,
            discount: 0,
            gst_rate: 0,
            total_sessions: 0,
            session_number: 0,
            final_price: 0,
          },
          summaryRow: {
            enabled: true,
            fields: ['final_price'],
          },
          columns: [
            {
              key: 'offering',
              title: 'Offering',
              dataIndex: 'offering_id',
              width: 200,
              maxWidth: 200,
              field: {
                name: 'offering_id',
                label: 'Offering',
                type: 'resourceSelect',
                required: true,
                placeholder: consultationOnly
                  ? 'Type consultation, online, new… to search'
                  : 'Select offering...',
                tooltip: consultationOnly
                  ? 'Only offerings whose name or description includes "consult" can be saved on a consultation invoice.'
                  : undefined,
                validation: {
                  required: (value: any, allValues: any) => {
                    // Only require if the row has any data
                    return (
                      value !== undefined && value !== null && value !== ''
                    );
                  },
                  message: 'Please select an offering',
                },
                searchSelectProps: {
                  resource: 'offerings',
                  optionValue: 'id',
                  searchFields: ['name', 'description', 'category'],
                  ...(consultationOnly
                    ? {
                        // Prefer text discovery: default list is consultation-related; typing e.g. "consultation" or "online" narrows via search fields (AND with this filter).
                        filters: [
                          {
                            field: 'name',
                            operator: 'contains',
                            value: CONSULTATION_NAME_SUBSTRING,
                          },
                        ],
                      }
                    : {}),
                  optionRender: (option: any) => {
                    if (!option) return '';
                    return `${option.name} - (₹${option.price})`;
                  },
                  optionTooltip: (option: any) => {
                    if (!option) return '';
                    return `
                      Name: ${option.name}
                      Description: ${option.description || 'N/A'}
                      Category: ${option.category}
                      Price: ₹${option.price}
                      GST: ${option.gst_rate || 0}%
                      Sessions: ${option.total_sessions || 0}
                      Duration: ${option.duration_in_mins} mins
                    `;
                  },
                },
              },
              onValueChange: (value, rowData, allRows, form) =>
                handleColumnValueChange(
                  'offering_id',
                  value,
                  rowData,
                  allRows,
                  form
                ),
            },
            {
              key: 'quantity',
              title: 'Quantity',
              dataIndex: 'quantity',
              width: 100,
              field: {
                name: 'quantity',
                label: 'Quantity',
                type: 'number',
                required: true,
                placeholder: '1',
                defaultValue: 1,
                validation: {
                  min: 1,
                  required: (value: any, allValues: any) => {
                    // Only require if offering is selected
                    return value !== undefined && value !== null && value > 0;
                  },
                  message: 'Quantity must be at least 1',
                },
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) =>
                handleColumnValueChange(
                  'quantity',
                  value,
                  rowData,
                  allRows,
                  form
                ),
            },
            {
              key: 'unit_price',
              title: 'Rate',
              dataIndex: 'unit_price',
              width: 100,
              field: {
                name: 'unit_price',
                label: 'Rate',
                type: 'number',
                align: 'right',
                format: 'decimal',
                decimalPlaces: 2,
                required: false,
                placeholder: '0.00',
                validation: { min: 0 },
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? `₹${numValue.toFixed(2)}` : '₹0.00';
              },
            },
            {
              key: 'discount',
              title: 'Discount %',
              dataIndex: 'discount',
              width: 120,
              field: {
                name: 'discount',
                label: 'Discount %',
                type: 'number',
                align: 'right',
                format: 'percentage',
                required: false,
                placeholder: '0',
                defaultValue: 0,
                validation: {
                  min: 0,
                  max: 100,
                  validator: (rule: any, value: any) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve(); // Allow empty values
                    }
                    const numValue = parseFloat(value);
                    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                      return Promise.reject(
                        'Discount must be between 0 and 100%'
                      );
                    }
                    return Promise.resolve();
                  },
                  message: 'Discount must be between 0 and 100%',
                },
              },
              onValueChange: (value, rowData, allRows, form) =>
                handleColumnValueChange(
                  'discount',
                  value,
                  rowData,
                  allRows,
                  form
                ),
            },
            {
              key: 'gst_rate',
              title: 'GST %',
              dataIndex: 'gst_rate',
              width: 80,
              field: {
                name: 'gst_rate',
                label: 'GST %',
                type: 'number',
                required: false,
                placeholder: '0.00',
                defaultValue: 0,
                validation: { min: 0 },
                align: 'right',
                format: 'percentage',
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? `${numValue.toFixed(2)}%` : '0.00%';
              },
            },
            {
              key: 'total_sessions',
              title: 'Sessions',
              dataIndex: 'total_sessions',
              width: 100,
              field: {
                name: 'total_sessions',
                label: 'Sessions',
                type: 'number',
                required: false,
                placeholder: '0',
                defaultValue: 0,
                validation: { min: 0, step: 1 },
                align: 'right',
                format: 'number',
                decimalPlaces: 0,
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? numValue.toFixed(2) : '0.00';
              },
            },
            {
              key: 'final_price',
              title: 'Final Price',
              dataIndex: 'final_price',
              width: 120,
              field: {
                name: 'final_price',
                label: 'Final Price',
                type: 'number',
                required: false,
                placeholder: '0.00',
                validation: { min: 0 },
                align: 'right',
                format: 'currency',
                decimalPlaces: 2,
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? `₹${numValue.toFixed(2)}` : '₹0.00';
              },
            },
          ],
          onSummaryChange: summary => {
            // This will be handled by the form's onSummaryChange
          },
          onRowDelete: (deletedRow: any, allRows: any[], form: any) => {
            // Immediate recalculation when a row is deleted
            setTimeout(() => {
              calculateInvoiceTotals(allRows, form);
            }, 0);
          },
        },
        layout: { span: 24 },
        // Add transform function for proper data loading in edit mode
        transform: (value: any) => {
          // Handle existing data when loading for edit
          if (Array.isArray(value) && value.length > 0) {
            // Process each item to ensure proper data structure
            const processedItems = value.map((item: any, index: number) => {
              // Ensure offering data is properly structured
              let offering = item.offering;
              if (!offering && item.offering_id) {
                // If offering object is missing but we have an ID, create a proper structure
                offering = {
                  id: item.offering_id,
                  name:
                    item.item_name ||
                    item.name ||
                    `Offering ID: ${item.offering_id}`,
                  description: item.item_description || '',
                  price: parseFloat(item.unit_price) || 0,
                  gst_rate: item.gst_rate,
                  total_sessions: item.total_sessions,
                  category: item.category || '',
                  duration_in_mins: item.duration_in_mins,
                };
              } else if (offering && !offering.id && item.offering_id) {
                // Ensure offering has proper ID
                offering = {
                  ...offering,
                  id: item.offering_id,
                };
              }

              const processedItem = {
                ...item,
                id: item.id || `temp_${Date.now()}_${index}`,
                offering_id: item.offering_id,
                offering: offering,
                // Store original item name for display
                item_name: offering?.name || item.item_name || item.name,
                item_description:
                  offering?.description || item.item_description || '',
                // Ensure all required fields have default values
                unit_price: formatNumber(
                  parseFloat(item.unit_price) ||
                    parseFloat(offering?.price) ||
                    0
                ),
                quantity: formatNumber(parseFloat(item.quantity) || 1),
                discount: formatNumber(parseFloat(item.discount) || 0),
                gst_rate: item.gst_rate || offering?.gst_rate,
                total_sessions:
                  item.total_sessions || offering?.total_sessions,
                session_number: formatNumber(parseFloat(item.session_number)),
                final_price: formatNumber(item.final_price),
              };
              return processedItem;
            });
            return processedItems;
          }
          return value || [];
        },
        onChange: (value: any, form: any) => {
          // Recalculate totals whenever the table data changes (including deletions)
          if (value && Array.isArray(value)) {
            // Add a small delay to ensure the table state is updated
            setTimeout(() => {
              calculateInvoiceTotals(value, form);
            }, 10);
          } else {
            // If no items, clear all totals using clinic_invoices schema structure
            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                total_rate: 0,
                total_amount: 0,
                overall_discount_rate: null,
                overall_discount_amount: null,
                gst: 0,
                bill_amount: 0,
                due_amount: 0,
              });
            }

            if (form && typeof form.setFields === 'function') {
              form.setFields([
                { name: 'total_rate', value: 0 },
                { name: 'total_amount', value: 0 },
                { name: 'overall_discount_rate', value: null },
                { name: 'overall_discount_amount', value: null },
                { name: 'gst', value: 0 },
                { name: 'bill_amount', value: 0 },
                { name: 'due_amount', value: 0 },
              ]);
            }
          }
        },
      },

      // Row 4: Financial Details (matching clinic_invoices schema)
      {
        name: 'total_rate',
        label: 'Total Rate',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true, // Read-only, calculated from items
        },
      },
      {
        name: 'gst',
        label: 'GST Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true,
        },
      },
      {
        name: 'total_amount',
        label: 'Total Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true,
        },
      },
      {
        name: 'overall_discount_rate',
        label: 'Overall Discount %',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          min: 0,
          max: 100,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          if (baseAmount > 0 && value > 0) {
            const discountAmount = formatNumber(
              (baseAmount * (value || 0)) / 100
            );
            // Calculate bill amount: totalAmount - overall discount
            const billAmount = formatNumber(totalAmount - discountAmount);

            form.setFieldsValue({
              overall_discount_amount: discountAmount,
              bill_amount: billAmount,
              due_amount: billAmount,
            });

            // Auto-update invoice status based on bill amount
            if (billAmount === 0) {
              form.setFieldsValue({
                invoice_status: 'PAID',
              });
            } else if (billAmount > 0) {
              const currentStatus = form.getFieldValue('invoice_status');
              if (currentStatus === 'PAID') {
                form.setFieldsValue({
                  invoice_status: 'UNPAID',
                });
              }
            }
          } else if (value === 0 || !value) {
            // If discount rate is cleared/set to 0, clear discount amount too
            form.setFieldsValue({
              overall_discount_amount: null,
            });

            // Recalculate without overall discount
            const invoiceItems = form.getFieldValue('clinic_invoice_items') || [];
            setTimeout(() => {
              calculateInvoiceTotals(invoiceItems, form);
            }, 50);
          }
        },
      },
      {
        name: 'overall_discount_amount',
        label: 'Overall Discount Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
        props: {
          min: 0,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          if (baseAmount > 0 && value > 0) {
            const discountRate = formatNumber(
              ((value || 0) * 100) / baseAmount
            );
            // Calculate bill amount: totalAmount - overall discount amount
            const billAmount = formatNumber(totalAmount - (value || 0));

            form.setFieldsValue({
              overall_discount_rate: discountRate,
              bill_amount: billAmount,
              due_amount: billAmount,
            });

            // Auto-update invoice status based on bill amount
            if (billAmount === 0) {
              form.setFieldsValue({
                invoice_status: 'PAID',
              });
            } else if (billAmount > 0) {
              const currentStatus = form.getFieldValue('invoice_status');
              if (currentStatus === 'PAID') {
                form.setFieldsValue({
                  invoice_status: 'UNPAID',
                });
              }
            }
          } else if (value === 0 || !value) {
            // If discount amount is cleared/set to 0, clear discount rate too
            form.setFieldsValue({
              overall_discount_rate: null,
            });

            // Recalculate without overall discount
            const invoiceItems = form.getFieldValue('clinic_invoice_items') || [];
            setTimeout(() => {
              calculateInvoiceTotals(invoiceItems, form);
            }, 50);
          }
        },
      },
      {
        name: 'bill_amount',
        label: 'Bill Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          min: 0,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          const billAmount = parseFloat(value) || 0;

          // When bill amount is manually changed, derive overall discount from the difference
          // Expected total without overall discount would be: sum of all final prices
          // But bill amount represents what user wants to charge
          // So overall discount = totalAmount - billAmount

          if (baseAmount > 0 && billAmount >= 0) {
            // Calculate overall discount amount
            const overallDiscountAmount = Math.max(
              0,
              formatNumber(totalAmount - billAmount)
            );

            // Calculate overall discount rate based on base amount (total_rate or total_amount)
            const overallDiscountRate =
              baseAmount > 0
                ? formatNumber((overallDiscountAmount * 100) / baseAmount)
                : 0;

            // Update form fields
            form.setFieldsValue({
              overall_discount_amount: overallDiscountAmount,
              overall_discount_rate: overallDiscountRate,
              due_amount: billAmount,
            });
          } else {
            // If no base amount, clear discount fields
            form.setFieldsValue({
              overall_discount_amount: 0,
              overall_discount_rate: 0,
              due_amount: billAmount,
            });
          }

          // Auto-update invoice status based on bill amount
          if (billAmount === 0) {
            // If bill amount is 0, automatically set status to PAID
            form.setFieldsValue({
              invoice_status: 'PAID',
            });
          } else if (billAmount > 0) {
            // If bill amount is greater than 0, set status to UNPAID
            const currentStatus = form.getFieldValue('invoice_status');
            if (currentStatus === 'PAID') {
              form.setFieldsValue({
                invoice_status: 'UNPAID',
              });
            }
          }
        },
      },
      {
        name: 'due_amount',
        label: 'Due Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true,
        },
      },
      {
        name: 'clinic_due_amount',
        label: 'Clinic Due Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
      },

      {
        name: 'invoice_status',
        label: 'Invoice Status',
        type: 'select',
        required: true,
        hidden: true,
        options: INVOICE_STATUS_OPTIONS,
        defaultValue: 'UNPAID',
        layout: { span: 0 },
        style: { display: 'none' },
        props: {
          size: 'middle',
        },
      },
    ],
    [userOptionRender, patientId, consultationOnly]
  );
};
